import React, { FC, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Row,
    Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";

import { AppUserApi } from "../../../api";
import { CustomBreadCrumb } from "../../../components/customBreadCrumb";
import { WeeklySummaryType } from "../../../DTO/app-user/food";
import { useAppNavigate, useAppParams } from "../../../Router/hook";
import { selectUser } from "../../../redux-config/entities/user";
import { useAppSelector } from "../../../redux-config/hooks";
import { formatUserId } from "../../../utils";
import "./style.scss";

const LIGHT_COLORS: Record<string, string> = {
    green: "#22C55E",
    amber: "#F59E0B",
    yellow: "#F59E0B",
    red: "#EF4444",
    no_data: "#94A3B8",
};

const PRIMARY_NUTRITION_KEYS = ["calories", "protein", "carbohydrates", "fat"];
const SECONDARY_NUTRITION_KEYS = [
    "fiber",
    "sugar",
    "sodium",
    "cholesterol",
    "saturated_fat",
    "trans_fat",
];

const prettifyKey = (key: string) =>
    key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const getLightColor = (light?: string) => LIGHT_COLORS[light || ""] || LIGHT_COLORS.red;

const getLightBadgeVariant = (light?: string) => {
    if (light === "green") return "success" as const;
    if (light === "amber" || light === "yellow") return "warning" as const;
    if (light === "no_data") return "secondary" as const;
    return "danger" as const;
};

const getMetricUnit = (key: string) => {
    if (key === "calories") return "kcal";
    if (key === "sodium" || key === "cholesterol") return "mg";
    return "g";
};

const getNumericValue = (source: Record<string, any>, key: string) => {
    const value = source?.[key];
    return typeof value === "number" ? value : Number(value || 0);
};

const formatMetricValue = (source: Record<string, any>, key: string) => {
    const value = getNumericValue(source, key);
    if (key === "calories") return value.toFixed(0);
    return value.toFixed(1);
};

const toPercent = (value: number, max: number) => {
    if (!max) return 0;
    return Math.max(0, Math.min(100, (value / max) * 100));
};

const getTrendIcon = (direction?: string) => {
    if (direction === "improving") return "up";
    if (direction === "declining") return "down";
    return "stable";
};

export const WeeklySummaryPage: FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [summary, setSummary] = useState<WeeklySummaryType | null>(null);

    const { userId, weekId } = useAppParams();
    const navigate = useAppNavigate();
    const user = useAppSelector(selectUser);

    const crumbs = [
        { name: "", href: "" },
        { name: "Dashboard", href: "/" },
        { name: "Users", href: "/app-user" },
        {
            name: `User ${formatUserId(userId)}`,
            href: `/app-user/${userId}/progress/detail`,
        },
        { name: "Weekly Summary", href: "#" },
    ];

    const fetchWeeklySummary = () => {
        setLoading(true);
        AppUserApi.WeeklySummary({
            appUser: user,
            userId: Number(userId),
            weekId: Number(weekId),
        })
            .then((data) => setSummary(data.weekly_summary))
            .catch((err) => {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: err || "Failed to load weekly summary",
                });
                navigate(-1);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchWeeklySummary();
    }, [userId, weekId]);

    const distribution = summary?.traffic_light_distribution;
    const totalDays = distribution?.total_days || 0;
    const noDataDays = useMemo(() => {
        if (!distribution) return 0;
        return Math.max(
            0,
            (distribution.total_days || 0) -
                ((distribution.green_days || 0) +
                    (distribution.amber_days || 0) +
                    (distribution.red_days || 0)),
        );
    }, [distribution]);

    if (loading) {
        return (
            <Container
                fluid
                className="py-5 d-flex justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (!summary || !summary.has_summary) {
        return (
            <Container fluid className="wfs-page py-4">
                <CustomBreadCrumb breadCrumbList={crumbs} />
                <Alert variant="warning" className="mt-3 mb-0">
                    <strong>No data available</strong> - No summary found for this week.
                </Alert>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Weekly Food Summary - Admin Panel</title>
            </Helmet>

            <Container fluid className="wfs-page py-3 py-md-4">
                <CustomBreadCrumb breadCrumbList={crumbs} />

                <Card className="wfs-header-card border-0 shadow-sm mt-3">
                    <Card.Body className="p-3 p-md-4">
                        <div className="wfs-header-row">
                            <div>
                                <div className="wfs-label">Weekly Summary</div>
                                <div className="wfs-title-row">
                                    <span className="wfs-emoji" aria-hidden="true">
                                        {summary.display.emoji || "-"}
                                    </span>
                                    <h1 className="wfs-title mb-0">{summary.display.title || "Week summary"}</h1>
                                </div>
                                <div className="wfs-subtitle">
                                    Week {summary.week_id} · Generated {formatDateTime(summary.generated_at)}
                                </div>
                            </div>

                            <div className="wfs-header-actions">
                                <Badge bg={getLightBadgeVariant(summary.display.light)} className="wfs-status-badge">
                                    {summary.display.light.toUpperCase()}
                                </Badge>
                                <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
                                    Back
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Row className="g-2 g-md-3 mt-1">
                    <Col xs={6} md={3}>
                        <Card className="wfs-kpi-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="wfs-kpi-label">Weekly Score</div>
                                <div className="wfs-kpi-value">{summary.score.percentage}%</div>
                                <div className="wfs-kpi-meta">
                                    {summary.score.total.toFixed(1)}/{summary.score.max} points
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="wfs-kpi-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="wfs-kpi-label">Trend</div>
                                <div className="wfs-kpi-value wfs-kpi-value-sm text-capitalize">
                                    {getTrendIcon(summary.trend.direction)} {summary.trend.direction}
                                </div>
                                <div className="wfs-kpi-meta">
                                    {summary.trend.score_change > 0 ? "+" : ""}
                                    {summary.trend.score_change.toFixed(1)} points
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="wfs-kpi-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="wfs-kpi-label">Days With Data</div>
                                <div className="wfs-kpi-value">{summary.traffic_light_distribution.days_with_data}</div>
                                <div className="wfs-kpi-meta">of {summary.traffic_light_distribution.total_days} days</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="wfs-kpi-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="wfs-kpi-label">Worst Category</div>
                                <div className="wfs-kpi-value wfs-kpi-value-sm text-capitalize">
                                    {summary.insights.worst_category || "-"}
                                </div>
                                <div className="wfs-kpi-meta">Next coaching focus</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="g-3 mt-1">
                    <Col xl={8}>
                        <Card className="wfs-section-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="wfs-section-head">
                                    <h2 className="wfs-section-title mb-0">Weekly Consistency</h2>
                                    <span className="wfs-section-note">
                                        G {summary.traffic_light_distribution.green_days} · A {summary.traffic_light_distribution.amber_days} · R {summary.traffic_light_distribution.red_days}
                                    </span>
                                </div>

                                <div className="wfs-segment-bar" aria-label="Weekly consistency distribution">
                                    <span
                                        className="wfs-segment wfs-segment-green"
                                        style={{ width: `${toPercent(summary.traffic_light_distribution.green_days, totalDays)}%` }}
                                    />
                                    <span
                                        className="wfs-segment wfs-segment-amber"
                                        style={{ width: `${toPercent(summary.traffic_light_distribution.amber_days, totalDays)}%` }}
                                    />
                                    <span
                                        className="wfs-segment wfs-segment-red"
                                        style={{ width: `${toPercent(summary.traffic_light_distribution.red_days, totalDays)}%` }}
                                    />
                                    {noDataDays > 0 && (
                                        <span
                                            className="wfs-segment wfs-segment-muted"
                                            style={{ width: `${toPercent(noDataDays, totalDays)}%` }}
                                        />
                                    )}
                                </div>

                                <div className="wfs-consistency-grid mt-3">
                                    <div className="wfs-consistency-item">
                                        <span className="wfs-dot" style={{ backgroundColor: LIGHT_COLORS.green }} />
                                        <span>Green</span>
                                        <strong>{summary.traffic_light_distribution.green_days}</strong>
                                    </div>
                                    <div className="wfs-consistency-item">
                                        <span className="wfs-dot" style={{ backgroundColor: LIGHT_COLORS.amber }} />
                                        <span>Amber</span>
                                        <strong>{summary.traffic_light_distribution.amber_days}</strong>
                                    </div>
                                    <div className="wfs-consistency-item">
                                        <span className="wfs-dot" style={{ backgroundColor: LIGHT_COLORS.red }} />
                                        <span>Red</span>
                                        <strong>{summary.traffic_light_distribution.red_days}</strong>
                                    </div>
                                    <div className="wfs-consistency-item">
                                        <span className="wfs-dot" style={{ backgroundColor: LIGHT_COLORS.no_data }} />
                                        <span>No data</span>
                                        <strong>{noDataDays}</strong>
                                    </div>
                                </div>

                                <div className="wfs-text-note mt-3">{summary.trend.description || "-"}</div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl={4}>
                        <Card className="wfs-section-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="wfs-section-head">
                                    <h2 className="wfs-section-title mb-0">Coaching Summary</h2>
                                </div>

                                <div className="wfs-guidance-block">
                                    <div className="wfs-guidance-label">Key Insight</div>
                                    <div className="wfs-guidance-text">
                                        {summary.insights.weekly_insight || "-"}
                                    </div>
                                </div>

                                <div className="wfs-chip-row">
                                    <span className="wfs-chip">
                                        Worst category: {summary.insights.worst_category || "-"}
                                    </span>
                                </div>

                                {summary.insights.focus_areas?.length > 0 && (
                                    <div className="wfs-guidance-block mb-0">
                                        <div className="wfs-guidance-label">Focus Areas</div>
                                        <ul className="wfs-list mb-0">
                                            {summary.insights.focus_areas.map((area, index) => (
                                                <li key={`focus-${index}`}>{area}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="g-3 mt-1">
                    <Col xl={6}>
                        <Card className="wfs-section-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="wfs-section-head">
                                    <h2 className="wfs-section-title mb-0">Category Averages</h2>
                                    <span className="wfs-section-note">Average score per category</span>
                                </div>

                                <div className="wfs-category-grid">
                                    {Object.entries(summary.category_averages).map(([category, data]) => (
                                        <div className="wfs-category-tile" key={category}>
                                            <div className="d-flex justify-content-between align-items-center gap-2">
                                                <div className="fw-semibold text-capitalize">{category}</div>
                                                <Badge bg={getLightBadgeVariant(data.light)}>
                                                    {String(data.light).toUpperCase()}
                                                </Badge>
                                            </div>
                                            <div className="wfs-category-score mt-2">
                                                {Number(data.avg_score || 0).toFixed(1)}
                                                <span>/25</span>
                                            </div>
                                            <div className="wfs-mini-bar mt-2" aria-hidden="true">
                                                <span
                                                    style={{
                                                        width: `${toPercent(Number(data.avg_score || 0), 25)}%`,
                                                        backgroundColor: getLightColor(data.light),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl={6}>
                        <Card className="wfs-section-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="wfs-section-head">
                                    <h2 className="wfs-section-title mb-0">Nutrition Overview</h2>
                                    <span className="wfs-section-note">Totals and daily averages</span>
                                </div>

                                <div className="wfs-subpanel-wrap">
                                    <div className="wfs-subpanel">
                                        <div className="wfs-subpanel-title">Weekly Totals</div>
                                        <div className="wfs-metric-grid">
                                            {PRIMARY_NUTRITION_KEYS.map((key) => (
                                                <div className="wfs-metric-card" key={`totals-${key}`}>
                                                    <div className="wfs-metric-label">{prettifyKey(key)}</div>
                                                    <div className="wfs-metric-value">
                                                        {formatMetricValue(summary.nutrition.totals as Record<string, any>, key)}
                                                    </div>
                                                    <div className="wfs-metric-unit">{getMetricUnit(key)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="wfs-subpanel">
                                        <div className="wfs-subpanel-title">Daily Averages</div>
                                        <div className="wfs-metric-grid">
                                            {PRIMARY_NUTRITION_KEYS.map((key) => (
                                                <div className="wfs-metric-card" key={`avg-${key}`}>
                                                    <div className="wfs-metric-label">{prettifyKey(key)}</div>
                                                    <div className="wfs-metric-value">
                                                        {formatMetricValue(
                                                            summary.nutrition.daily_averages as Record<string, any>,
                                                            key,
                                                        )}
                                                    </div>
                                                    <div className="wfs-metric-unit">{getMetricUnit(key)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <details className="wfs-details mt-3">
                                    <summary>More Nutrition Metrics</summary>
                                    <div className="wfs-details-body">
                                        <Row className="g-2">
                                            <Col md={6}>
                                                <div className="wfs-detail-list">
                                                    <div className="wfs-detail-title">Weekly Totals</div>
                                                    {SECONDARY_NUTRITION_KEYS.map((key) => (
                                                        <div className="wfs-detail-row" key={`total-${key}`}>
                                                            <span>{prettifyKey(key)}</span>
                                                            <span>
                                                                {formatMetricValue(
                                                                    summary.nutrition.totals as Record<string, any>,
                                                                    key,
                                                                )} {getMetricUnit(key)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="wfs-detail-list">
                                                    <div className="wfs-detail-title">Daily Averages</div>
                                                    {SECONDARY_NUTRITION_KEYS.map((key) => (
                                                        <div className="wfs-detail-row" key={`avg2-${key}`}>
                                                            <span>{prettifyKey(key)}</span>
                                                            <span>
                                                                {formatMetricValue(
                                                                    summary.nutrition.daily_averages as Record<string, any>,
                                                                    key,
                                                                )} {getMetricUnit(key)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </details>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};
